// src/components/Administration.js - CRUD Only (Create, Edit, Delete)
import React, { useState, useEffect } from 'react';
import EventForm from './EventForm';
import EventList from './EventList';
import CouplesList from './CouplesList';
import DancerAllocation from './DancerAllocation';
import BulkEntries from './BulkEntries';
import ClubsList from './ClubsList';
import ErrorBoundary from './ErrorBoundary';
import { useCompetition } from '../competition/CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import auditLogger from '../utils/auditLogger';
import { calculateStartTimes, saveCompetitionStartTime, formatTime, generateScheduleSummary, getEventsWithStartTimes } from '../utils/startTimeCalculator';
import { getDancersList, normalizeEvent } from '../utils/fieldCompatibility';
import './Administration.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const Administration = () => {
  const { selectedCompetition } = useCompetition();
  const { currentUser } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'clubs', 'dancers', 'allocation', or 'bulk'
  const [isCalculating, setIsCalculating] = useState(false);
  const [scheduleSummary, setScheduleSummary] = useState(null);
  const [showStartTimeModal, setShowStartTimeModal] = useState(false);
  const [competitionStartTime, setCompetitionStartTime] = useState('');
  const [currentCompetitionStartTime, setCurrentCompetitionStartTime] = useState(null);

  // Event combination state
  const [showCombineModal, setShowCombineModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [combineLoading, setCombineLoading] = useState(false);
  const [combineError, setCombineError] = useState(null);

  // Event combination search states
  const [combineSearchQuery, setCombineSearchQuery] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);

  // Function to fetch competition details to get official start time
  const fetchCompetitionDetails = async () => {
    if (!selectedCompetition) return;

    try {
      const response = await fetch(`https://${_host}:6003/api/${_company}/competitions/${selectedCompetition.id}`);
      if (response.ok) {
        const competition = await response.json();
        if (competition.official_start_time) {
          setCurrentCompetitionStartTime(new Date(competition.official_start_time));
        } else {
          setCurrentCompetitionStartTime(null);
        }
      }
    } catch (error) {
      console.error('Error fetching competition details:', error);
      setCurrentCompetitionStartTime(null);
    }
  };

  // Function to fetch events from the API
  const fetchEvents = async () => {
    try {
      // Check if we have a selected competition
      if (!selectedCompetition) {
        setError('No competition selected');
        setLoading(false);
        return;
      }

      // Also fetch competition details for start time
      fetchCompetitionDetails();

      let url;
      let data;

      // Try with competition filtering first
      try {
        url = `https://${_host}:6003/api/${_company}/events?competitionId=${selectedCompetition.id}`;
        const response = await fetch(url);

        if (response.ok) {
          data = await response.json();
          setEvents(data);
          setLoading(false);
          return;
        }
      } catch (compFilterError) {
        console.log('Competition filtering not supported, trying fallback...');
      }

      // Fallback to all events (for servers without competition support)
      console.log('Fetching all events (competition filtering not available)');
      url = `https://${_host}:6003/api/${_company}/events`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      data = await response.json();

      // Filter events locally if server doesn't support it
      const filteredEvents = data.filter(event =>
        !selectedCompetition || event.competitionId === selectedCompetition.id
      );

      setEvents(filteredEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  // Fetch events when component mounts or when competition changes
  useEffect(() => {
    fetchEvents();

    // Set up polling to refresh events periodically
    const interval = setInterval(fetchEvents, 5000); // Poll every 5 seconds

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [selectedCompetition]); // Add selectedCompetition to dependency array

  // Event combination handlers
  const handleCombineSuccess = () => {
    // Force refresh to show combined events
    fetchEvents();
    setShowCombineModal(false);
    setCombineError(null);
    setSuccess('Events combined successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleSplitSuccess = () => {
    // Force refresh to show restored events
    fetchEvents();
    setShowSplitModal(false);
    setCombineError(null);
    setSuccess('Event split successfully!');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Helper function to check if two events can be combined
  const canCombineEvents = (event1, event2) => {
    // Extract level and style from both events
    const level1 = event1.level || event1.danceLevel;
    const level2 = event2.level || event2.danceLevel;
    const style1 = event1.dance_style || event1.danceStyle || event1.style;
    const style2 = event2.dance_style || event2.danceStyle || event2.style;

    // Must have same level and same style
    return level1 === level2 && style1 === style2;
  };

  // Helper function to generate combined event name
  const generateCombinedEventName = (event1, event2) => {
    const age1 = event1.age_group || event1.ageGroup;
    const age2 = event2.age_group || event2.ageGroup;
    const level = event1.level || event1.danceLevel;
    const style = event1.dance_style || event1.danceStyle || event1.style;

    // Sort age groups alphabetically (younger first)
    const ageGroups = [age1, age2].sort();
    return `${ageGroups[0]}/${ageGroups[1]} Level ${level} ${style}`;
  };

  // Function to handle event creation
  const handleCreateEvent = async (eventData) => {
    try {
      console.log("Event created successfully:", eventData);
      fetchEvents();
      return eventData.id;
    } catch (error) {
      console.error("Error in handleCreateEvent:", error);
      throw error;
    }
  };

  // Function to handle event reordering
  const handleReorderEvents = async (reorderedEvents) => {
    try {
      // Update all events with new item numbers
      const updatePromises = reorderedEvents.map((event, index) => {
        return fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ itemNumber: index + 1 })
        });
      });

      await Promise.all(updatePromises);
      fetchEvents();
    } catch (err) {
      console.error('Error reordering events:', err);
      setError('Failed to reorder events. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDeleteEvent = async (eventId, eventName) => {
    try {
      const confirmDelete = window.confirm(
        `Are you sure you want to delete the event "${eventName}"? This action cannot be undone.`
      );

      if (!confirmDelete) {
        return;
      }

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/${eventId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete event');
      }

      // Log event deletion
      if (currentUser) {
        auditLogger.logDeleteEvent(
          currentUser.username || currentUser.id,
          eventName,
          eventId
        );
      }

      // After successful deletion, resequence item numbers
      await resequenceEventNumbers();

    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err.message || 'Failed to delete event. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Function to resequence event item numbers after deletion
  const resequenceEventNumbers = async () => {
    try {
      // First, fetch the updated events list
      const eventsResponse = await fetch(`https://${_host}:6003/api/${_company}/events`);
      if (!eventsResponse.ok) {
        throw new Error('Failed to fetch events for resequencing');
      }

      const updatedEvents = await eventsResponse.json();

      // Filter events for the current competition
      const competitionEvents = updatedEvents.filter(event =>
        (event.competitionId === selectedCompetition?.id) || (event.competition_id === selectedCompetition?.id)
      );

      // Sort events by current item number (put events without item numbers at the end)
      const sortedEvents = [...competitionEvents].sort((a, b) => {
        const itemA = a.itemNumber || a.item_number || 999999;
        const itemB = b.itemNumber || b.item_number || 999999;
        return itemA - itemB;
      });

      // Resequence item numbers (1, 2, 3, 4, etc.)
      const resequencePromises = sortedEvents.map((event, index) => {
        const newItemNumber = index + 1;

        // Only update if the item number has changed
        if ((event.itemNumber || event.item_number) !== newItemNumber) {
          return fetch(`https://${_host}:6003/api/${_company}/events/${event.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              itemNumber: newItemNumber,
              item_number: newItemNumber
            })
          });
        }
        return Promise.resolve();
      });

      // Wait for all updates to complete
      await Promise.all(resequencePromises);

      // Log resequencing if user is logged in
      if (currentUser) {
        auditLogger.logEvent(
          currentUser.username || currentUser.id,
          'resequence_item_numbers',
          'Event item numbers resequenced after deletion',
          {
            totalEvents: sortedEvents.length,
            competitionId: selectedCompetition?.id
          }
        );
      }

      // Fetch events to show updated sequence
      fetchEvents();

      // Show success message
      setSuccess('Event deleted and item numbers resequenced successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      console.error('Error resequencing event numbers:', err);
      setError(err.message || 'Failed to resequence item numbers. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Function to calculate and save start times
  const handleCalculateStartTimes = async () => {
    if (events.length === 0) {
      setError('No events available to calculate start times.');
      return;
    }

    // Check if all events have dances defined (skip non-dance events)
    const eventsWithoutDances = events.filter(event => {
      // Import the helper function to detect non-dance events
      const isNonDanceEvent = (event) => {
        // Check explicit flag first (for future events created with the new form)
        if (event.isNonDanceEvent === true) {
          return true;
        }

        // Legacy detection: events with no dances, no age/level/style info are likely non-dance events
        const hasNoDances = (!event.dances || event.dances.length === 0);
        const hasNullOrMissingDanceInfo = (
          (event.ageGroup === null || event.ageGroup === undefined || event.ageGroup === "") &&
          (event.level === null || event.level === undefined || event.level === "") &&
          (event.danceStyle === null || event.danceStyle === undefined || event.danceStyle === "")
        );
        const hasNoDancerInfo = (!event.dancers || event.dancers.length === 0);

        // Also check if the name suggests it's a break/non-dance event
        const nameIndicatesNonDance = event.name && (
          event.name.toUpperCase().includes('BREAK') ||
          event.name.toUpperCase().includes('LUNCH') ||
          event.name.toUpperCase().includes('INTERVAL') ||
          event.name.toUpperCase().includes('ENTERTAINMENT') ||
          event.name.toUpperCase().includes('OPENING') ||
          event.name.toUpperCase().includes('CLOSING')
        );

        // Detailed debug logging
        console.log(`🔍 DEBUG for "${event.name}":`, {
          hasNoDances,
          hasNullOrMissingDanceInfo,
          nameIndicatesNonDance,
          hasNoDancerInfo,
          ageGroup: event.ageGroup,
          level: event.level,
          danceStyle: event.danceStyle,
          dancersLength: event.dancers ? event.dancers.length : 0,
          result: hasNoDances && (hasNullOrMissingDanceInfo || nameIndicatesNonDance) && hasNoDancerInfo
        });

        // If the name clearly indicates it's a non-dance event, prioritize that over dancer assignment
        if (nameIndicatesNonDance && hasNoDances) {
          return true;
        }

        return hasNoDances && hasNullOrMissingDanceInfo && hasNoDancerInfo;
      };

      const isNonDance = isNonDanceEvent(event);
      const isDanceEventWithNoDances = !isNonDance && (!event.dances || event.dances.length === 0);

      // Debug logging
      console.log(`🔍 Event Check: "${event.name}"`, {
        isNonDance,
        isDanceEventWithNoDances,
        dances: event.dances,
        dancesLength: event.dances ? event.dances.length : 0,
        ageGroup: event.ageGroup,
        level: event.level,
        danceStyle: event.danceStyle
      });

      // Skip non-dance events from the dance check
      if (isNonDance) {
        return false; // This event doesn't need dances
      }

      // For dance events, check if they have dances defined
      return isDanceEventWithNoDances;
    });

    if (eventsWithoutDances.length > 0) {
      const eventNames = eventsWithoutDances.map(e => e.name).join(', ');
      setError(`Cannot calculate start times: ${eventsWithoutDances.length} dance events have no dances defined. Please add dances to: ${eventNames}`);
      return;
    }

    setShowStartTimeModal(true);
  };

  // Function to confirm and execute start time calculation
  const confirmCalculateStartTimes = async () => {
    if (!selectedCompetition) {
      setError('No competition selected. Please select a competition first.');
      return;
    }

    setIsCalculating(true);
    setError(null);

    try {
      // Parse competition start time
      let startTime = null;
      if (competitionStartTime) {
        startTime = new Date(competitionStartTime);
        if (isNaN(startTime.getTime())) {
          throw new Error('Invalid competition start time format');
        }
      } else {
        // Default to 9:00 AM today if not specified
        startTime = new Date();
        startTime.setHours(9, 0, 0, 0);
      }

      // Calculate start times for all events (preview only)
      const eventsWithStartTimes = calculateStartTimes(events, startTime);

      // Save official start time to competition
      await saveCompetitionStartTime(selectedCompetition.id, startTime, _host, _company);

      // Update current competition start time state
      setCurrentCompetitionStartTime(startTime);

      // Generate schedule summary
      const summary = generateScheduleSummary(eventsWithStartTimes);
      setScheduleSummary(summary);

      // Refresh events to show updated start times (by recalculating based on competition start time)
      fetchEvents();

      // Log the action
      if (currentUser) {
        auditLogger.log(
          currentUser.username || currentUser.id,
          'set_competition_start_time',
          'administration',
          null,
          null,
          null,
          {
            competitionId: selectedCompetition.id,
            competitionName: selectedCompetition.name,
            officialStartTime: startTime.toISOString(),
            totalEvents: events.length,
            totalDuration: summary.totalDuration,
            endTime: summary.endTime
          }
        );
      }

      setShowStartTimeModal(false);
      setCompetitionStartTime('');

      // Show success message
      const successMsg = `Competition start time set to ${formatTime(startTime)}. ${events.length} events scheduled. Competition ends at ${formatTime(summary.endTime)}.`;
      setError(null);

      // Display success message
      const successBanner = document.createElement('div');
      successBanner.className = 'success-banner';
      successBanner.textContent = successMsg;
      document.querySelector('.administration-page').insertBefore(successBanner, document.querySelector('.main-container'));

      setTimeout(() => {
        if (successBanner.parentNode) {
          successBanner.parentNode.removeChild(successBanner);
        }
      }, 5000);

    } catch (err) {
      console.error('Error setting competition start time:', err);
      setError(err.message || 'Failed to set competition start time. Please try again.');
    } finally {
      setIsCalculating(false);
    }
  };

  // Helper functions for event combination
  const getFilteredEventsForCombination = () => {
    return events.filter(event => {
      // Filter out non-dance events and already combined events
      const basicFilter = !event.is_combined &&
                         !event.name.toUpperCase().includes('BREAK') &&
                         !event.name.toUpperCase().includes('LUNCH') &&
                         !event.name.toUpperCase().includes('INTERVAL') &&
                         (event.level || event.danceLevel) &&
                         (event.dance_style || event.danceStyle || event.style);

      if (!basicFilter) return false;

      // Apply search filters
      const eventName = event.name.toLowerCase();
      const matchesName = !combineSearchQuery || eventName.includes(combineSearchQuery.toLowerCase());

      return matchesName;
    });
  };

  const handleEventSelection = (eventId) => {
    setSelectedEvents(prev => {
      if (prev.includes(eventId)) {
        return prev.filter(id => id !== eventId);
      } else if (prev.length < 2) {
        return [...prev, eventId];
      } else {
        // If already 2 selected, replace the first one
        return [prev[1], eventId];
      }
    });
  };

  const canCombineSelected = () => {
    if (selectedEvents.length !== 2) return false;

    const [event1Id, event2Id] = selectedEvents;
    const event1 = events.find(e => e.id === event1Id);
    const event2 = events.find(e => e.id === event2Id);

    if (!event1 || !event2) return false;

    // Check if they have same level and style but different age groups
    const level1 = event1.level || event1.danceLevel;
    const level2 = event2.level || event2.danceLevel;
    const style1 = event1.dance_style || event1.danceStyle || event1.style;
    const style2 = event2.dance_style || event2.danceStyle || event2.style;
    const ageGroup1 = event1.age_group || event1.ageGroup;
    const ageGroup2 = event2.age_group || event2.ageGroup;

    return level1 === level2 &&
           style1 === style2 &&
           ageGroup1 !== ageGroup2;
  };

  const clearAllSearchFilters = () => {
    setCombineSearchQuery('');
    setSelectedEvents([]);
  };

  // Function to combine events via API
  const handleCombineEvents = async () => {
    if (selectedEvents.length !== 2) {
      setCombineError('Please select exactly 2 events to combine');
      return;
    }

    if (!canCombineSelected()) {
      setCombineError('Selected events cannot be combined. They must have the same Level and Style but different Age Groups.');
      return;
    }

    // Additional validation: Check if events are active
    const [event1Id, event2Id] = selectedEvents;
    const event1 = events.find(e => e.id === event1Id);
    const event2 = events.find(e => e.id === event2Id);

    if (!event1 || !event2) {
      setCombineError('One or both selected events not found. Please try again.');
      return;
    }

    try {
      setCombineLoading(true);
      setCombineError(null);

      const requestData = {
        event1_id: selectedEvents[0],
        event2_id: selectedEvents[1],
        competitionId: selectedCompetition?.id
      };

      console.log('Sending combine request:', requestData);

      const response = await fetch(`https://${_host}:6003/api/${_company}/events/combine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      console.log('Combine response status:', response.status);
      console.log('Combine response headers:', response.headers);

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
          console.error('Server error response:', errorData);
        } catch (parseError) {
          const errorText = await response.text();
          console.error('Server error text:', errorText);
          errorData = { message: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        throw new Error(errorData.message || errorData.error || 'Failed to combine events');
      }

      const result = await response.json();

      // Refresh events list to show the combined event
      await fetchEvents();

      // Close modal and reset
      setShowCombineModal(false);
      clearAllSearchFilters();

      setSuccess(`Events combined successfully! New event: ${result.combined_event.name}`);
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      console.error('Error combining events:', err);
      setCombineError(err.message || 'Failed to combine events. Please try again.');
    } finally {
      setCombineLoading(false);
    }
  };

  return (
    <div className="administration-page">
      <div className="page-header">
        <h1>Administration</h1>
        <p className="page-subtitle">Create, edit, and remove events and dancers</p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {success && <div className="success-banner">{success}</div>}

      <div className="main-container">
        <div className="admin-tabs-container">
          <div className="admin-tabs">
            <button
              className={`admin-tab ${activeTab === 'events' ? 'active' : ''}`}
              onClick={() => setActiveTab('events')}
            >
              <span className="tab-icon">📅</span>
              <span className="tab-label">Events</span>
            </button>
            <button
              className={`admin-tab ${activeTab === 'clubs' ? 'active' : ''}`}
              onClick={() => setActiveTab('clubs')}
            >
              <span className="tab-icon">🏢</span>
              <span className="tab-label">Clubs</span>
            </button>
            <button
              className={`admin-tab ${activeTab === 'dancers' ? 'active' : ''}`}
              onClick={() => setActiveTab('dancers')}
            >
              <span className="tab-icon">👥</span>
              <span className="tab-label">Dancers</span>
            </button>
            <button
              className={`admin-tab ${activeTab === 'allocation' ? 'active' : ''}`}
              onClick={() => setActiveTab('allocation')}
            >
              <span className="tab-icon">🔗</span>
              <span className="tab-label">Event/Dancer Allocation</span>
            </button>
            <button
              className={`admin-tab ${activeTab === 'bulk' ? 'active' : ''}`}
              onClick={() => setActiveTab('bulk')}
            >
              <span className="tab-icon">📝</span>
              <span className="tab-label">Bulk Entries</span>
            </button>
          </div>
        </div>

        <div className="admin-content-container">
          {activeTab === 'events' ? (
            <div className="admin-content single-column">
              <div className="events-controls">
                <button
                  className="calculate-start-times-btn"
                  onClick={handleCalculateStartTimes}
                  disabled={loading || events.length === 0}
                  title="Calculate estimated start times for all events"
                >
                  <span className="btn-icon">⏰</span>
                  Calculate Start Times
                </button>
                <div className="events-info">
                  {events.length > 0 && (
                    <span className="events-count-info">
                      {events.length} events • {events.filter(e => e.startTime).length} with start times • {events.filter(e => e.is_combined).length} combined
                    </span>
                  )}
                </div>
              </div>
              <div className="crud-section">
                <EventForm onCreateEvent={handleCreateEvent} existingEvents={events} />
                <EventList
                  events={events}
                  loading={loading}
                  onSelectEvent={null}
                  selectedEventId={null}
                  onDeleteEvent={handleDeleteEvent}
                  onReorderEvents={handleReorderEvents}
                  showDeleteOnly={true}
                  competitionStartTime={currentCompetitionStartTime}
                />
              </div>
            </div>
          ) : activeTab === 'clubs' ? (
            <div className="admin-content single-column">
              <ClubsList />
            </div>
          ) : activeTab === 'dancers' ? (
            <div className="admin-content single-column">
              <CouplesList />
            </div>
          ) : activeTab === 'bulk' ? (
            <div className="admin-content single-column">
              <ErrorBoundary>
                <BulkEntries />
              </ErrorBoundary>
            </div>
          ) : (
            <div className="admin-content single-column">
              <DancerAllocation />
            </div>
          )}
        </div>
      </div>

      {/* Start Time Calculation Modal */}
      {showStartTimeModal && (
        <div className="modal-overlay" onClick={() => setShowStartTimeModal(false)}>
          <div className="modal-content start-time-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Calculate Event Start Times</h3>
              <button className="modal-close-btn" onClick={() => setShowStartTimeModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="calculation-info">
                <p>This will set the official start time for "{selectedCompetition?.name || 'Selected Competition'}" and calculate estimated start times for all {events.length} events based on:</p>
                <ul>
                  <li>Each dance takes 1 minute 30 seconds</li>
                  <li>10 second break between dances</li>
                  <li>10 minute break between events</li>
                </ul>
                <p><strong>Note:</strong> The competition start time will be saved to the competition record. Event start times will be calculated dynamically based on this official time.</p>
              </div>

              <div className="competition-start-time">
                <label htmlFor="competition-start-time">
                  Competition Start Time:
                </label>
                <input
                  id="competition-start-time"
                  type="datetime-local"
                  value={competitionStartTime}
                  onChange={(e) => setCompetitionStartTime(e.target.value)}
                  placeholder="Default: 9:00 AM today"
                />
                <small>Official start time for the competition (defaults to 9:00 AM today if not specified)</small>
              </div>

              {scheduleSummary && (
                <div className="schedule-preview">
                  <h4>Schedule Preview</h4>
                  <div className="preview-stats">
                    <div className="stat-item">
                      <span className="stat-label">Total Events:</span>
                      <span className="stat-value">{scheduleSummary.totalEvents}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Competition Ends:</span>
                      <span className="stat-value">{formatTime(scheduleSummary.endTime)}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Duration:</span>
                      <span className="stat-value">
                        {Math.floor(scheduleSummary.totalDuration / 3600)}h {Math.floor((scheduleSummary.totalDuration % 3600) / 60)}m
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowStartTimeModal(false)}
                disabled={isCalculating}
              >
                Cancel
              </button>
              <button
                className="confirm-btn"
                onClick={confirmCalculateStartTimes}
                disabled={isCalculating}
              >
                {isCalculating ? (
                  <>
                    <span className="spinner"></span>
                    Calculating...
                  </>
                ) : (
                  'Calculate & Save Start Times'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Combine Modal */}
      {showCombineModal && (
        <div className="modal-overlay" onClick={() => setShowCombineModal(false)}>
          <div className="modal-content event-combine-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Combine Events</h3>
              <button className="modal-close-btn" onClick={() => {
                setShowCombineModal(false);
                clearAllSearchFilters();
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="combine-instructions">
                <p>Select two events with the <strong>same Level and Dance Style</strong> to combine them.</p>
                <p>Events must have different Age Groups but the same Level and Style.</p>
              </div>

              {/* Search Controls */}
              <div className="simple-search-controls">
                <div className="search-field">
                  <label htmlFor="combine-search-name">Search Events</label>
                  <input
                    id="combine-search-name"
                    type="text"
                    placeholder="Search event names..."
                    value={combineSearchQuery}
                    onChange={(e) => setCombineSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
                {combineSearchQuery && (
                  <div className="search-actions">
                    <button
                      className="clear-search-btn"
                      onClick={clearAllSearchFilters}
                    >
                      Clear Search
                    </button>
                    <div className="search-results-count">
                      {getFilteredEventsForCombination().length} events found
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Events Summary */}
              {selectedEvents.length > 0 && (
                <div className="selected-events-summary">
                  <h4>Selected Events ({selectedEvents.length}/2):</h4>
                  <div className="selected-events-list">
                    {selectedEvents.map(eventId => {
                      const event = events.find(e => e.id === eventId);
                      return event ? (
                        <div key={eventId} className="selected-event-item">
                          <span className="selected-event-name">{event.name}</span>
                          <button
                            className="remove-selection-btn"
                            onClick={() => handleEventSelection(eventId)}
                          >
                            ×
                          </button>
                        </div>
                      ) : null;
                    })}
                  </div>
                  {selectedEvents.length === 2 && (
                    <div className="combination-status">
                      {canCombineSelected() ? (
                        <span className="status-success">✓ Events can be combined</span>
                      ) : (
                        <span className="status-error">✗ Events cannot be combined - must have same Level and Style but different Age Groups</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="combine-preview">
                <h4>Available Events for Combination:</h4>
                <div className="events-grid">
                  {getFilteredEventsForCombination().map(event => (
                    <div
                      key={event.id}
                      className={`event-card ${selectedEvents.includes(event.id) ? 'selected' : ''}`}
                      onClick={() => handleEventSelection(event.id)}
                    >
                      <div className="event-selection-indicator">
                        {selectedEvents.includes(event.id) && '✓'}
                      </div>
                      <div className="event-name">{event.name}</div>
                      <div className="event-details">
                        <span className="event-age">{event.age_group || event.ageGroup}</span>
                        <span className="event-level">{event.level || event.danceLevel}</span>
                        <span className="event-style">{event.dance_style || event.danceStyle || event.style}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {getFilteredEventsForCombination().length === 0 && (
                  <div className="no-events-found">
                    <p>No events found matching your search criteria.</p>
                    <p>Try adjusting your filters or clearing them to see all available events.</p>
                  </div>
                )}
              </div>
            </div>

              {/* Error Display */}
              {combineError && (
                <div className="combine-error">
                  <span className="error-icon">⚠️</span>
                  {combineError}
                </div>
              )}

            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => {
                setShowCombineModal(false);
                clearAllSearchFilters();
                setCombineError(null);
              }}>
                Cancel
              </button>
              <button
                className="combine-btn"
                disabled={selectedEvents.length !== 2 || !canCombineSelected() || combineLoading}
                onClick={handleCombineEvents}
              >
                {combineLoading ? (
                  <>
                    <span className="spinner"></span>
                    Combining...
                  </>
                ) : (
                  `Combine ${selectedEvents.length} Event${selectedEvents.length !== 1 ? 's' : ''}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Split Modal */}
      {showSplitModal && (
        <div className="modal-overlay" onClick={() => setShowSplitModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Split Combined Event</h3>
              <button className="modal-close-btn" onClick={() => setShowSplitModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="split-instructions">
                <p>Select a <strong>combined event</strong> to split it back into the original events.</p>
                <p>This will restore the original events and remove the combined event.</p>
              </div>
              <div className="split-preview">
                <h4>Combined Events Available for Splitting:</h4>
                <div className="events-grid">
                  {events.filter(event => event.is_combined).map(event => (
                    <div key={event.id} className="event-card combined">
                      <div className="event-name">{event.name}</div>
                      <div className="event-details">
                        <span className="combined-indicator">🔗 Combined</span>
                        <span className="event-count">{getDancersList(event)?.length || 0} dancers</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => setShowSplitModal(false)}>
                  Cancel
                </button>
                <button
                  className="split-btn"
                  onClick={() => {
                    // For now, just close the modal
                    setShowSplitModal(false);
                    setSuccess('Event split interface coming soon!');
                    setTimeout(() => setSuccess(null), 3000);
                  }}
                >
                  Split Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Administration;